/*
 
Required item properties: 

Raw properties:

citySlug
countrySlug
distanceBeach
freeinternet
id
internet
maxSleeps
minPrice
name
numBathrooms
numBedrooms
pictures[0].thumbnailLarge
poolType
regionSlug
reviews.length
reviewScore 
villaUri 
videoUrl  

Added properties:

accommodation_of_the_week
currency.conversion_rate
new_online
recommended
 
 */

import React,{ useRef, useEffect, useState} from "react";
import styled from "@emotion/styled";
import { VillaTileIcons } from "./VillaTileIcons";
import { VillaTileBreadcrumb } from "./VillaTileBreadcrumb";
import { VillaTilePriceCTA } from "./VillaTilePriceCTA";
import { VillaTileImage } from "./VillaTileImage";
import { VillaTileName } from "./VillaTileName";
import { VillaTileOpportunity } from "./VillaTileOpportunity";
import useIntersection from "./useIntersection";

import colors from '../../../sass/brand_palette.scss';

const VillaContainer = styled.div`
    padding: 1em;
    max-width: ${( {splitWidth} ) => {
        let mwidth = splitWidth === true ? '50%' : '33.333333%'; 
        return `${mwidth}`;
    }};
    overflow: hidden;
    flex: ${( {splitWidth} ) => {
        let flex = splitWidth === true ? '0 0 50%' : '0 0 33.333333%'; 
        return `${flex}`;
    }};
    
    @media (max-width:1300px){
        flex: 0 0 50%;
        max-width: 50%;
    }
    @media (max-width:767px){
        margin-bottom: 2em;
        padding: 0;
        flex: 0 0 100%;
        max-width:100%;
    }
`;

const opportunityStyleDesktop = {
    boxShadow: "inset 0px 0px 0px 8px #fff",
    backgroundColor: colors.orange1
};

const opportunityStyleMobile = {
    border: "5px solid " + colors.orange1
}
const INTERSECT_ONLY_ONCE = true;

export const VillaTile = (props) => {        
    const data = props.data;
    const gaEventType = props.gaEventType
    const opportunityStyle = (props.mobile && !props.tablet) ?  opportunityStyleMobile : opportunityStyleDesktop;
    const ref = useRef();
    const isInViewPort = useIntersection(ref, 0.5, 500, INTERSECT_ONLY_ONCE);

    const getEventType = () => {
        switch (gaEventType){
            case 'search':
                return 'Overview';
            break;
            case 'home':
                return 'Home';
            break;
            case 'suggestions':
                return 'Suggestions'
            break;
        }
    }

    const sendClickEvent = (data) => {
        const event = {
            event: "productClick",
            ecommerce: {
                value: data.priceSum > 0 ? data.priceSum.toFixed(2) : ((data.minPrice !== null) ? data.minPrice.toFixed(2) : 0),
                currencyCode: "EUR",
                click: {
                    actionField: {
                        list: getEventType(),
                    },
                    products: [{
                        id: data.id,
                        name: data.name,
                        price: data.priceSum > 0 ? data.priceSum.toFixed(2) : ((data.minPrice !== null) ? data.minPrice.toFixed(2) : 0),
                        brand: data.region,
                        category: data.city,
                        position: props.position
                    }]
                }
            }
        }
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ecommerce: null});
        window.dataLayer.push(event);

        const ga4Event = {
            event: "select_item",
            ecommerce: {
                value: data.priceSum > 0 ? data.priceSum.toFixed(2) : ((data.minPrice !== null) ? data.minPrice.toFixed(2) : 0),
                currency: "EUR",
                items: [{
                    item_id: data.id,
                    id: data.id,
                    item_name: data.name,
                    currency: "EUR",
                    price: data.priceSum > 0 ? data.priceSum.toFixed(2) : ((data.minPrice !== null) ? data.minPrice.toFixed(2) : 0),
                    item_brand: data.region,
                    item_category: data.city,
                    index: props.position,
                    item_list_id: getEventType(),
                    item_list_name: getEventType(),
                    index: props.position,
                    quantity: 1
                }]
            }
        }
        window.dataLayer.push({ecommerce: null});
        window.dataLayer.push(ga4Event);

        return true;
    }

    const sendViewportEvent = (data) => {
        const event = {
            event: "productImpression",
            ecommerce: {
                value: data.priceSum > 0 ? data.priceSum.toFixed(2) : ((data.minPrice !== null) ? data.minPrice.toFixed(2) : 0),
                currencyCode: "EUR",
                impressions: [{
                    id: data.id,
                    name: data.name,
                    price: data.priceSum > 0 ? data.priceSum.toFixed(2) : ((data.minPrice !== null) ? data.minPrice.toFixed(2) : 0),
                    brand: data.region,
                    category: data.city,
                    position: props.position,
                    list: getEventType()
                }],
            }
        }
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ecommerce: null});
        window.dataLayer.push(event);

        const ga4Event = {
            event: "view_item_list",
            ecommerce: {
                value: data.priceSum > 0 ? data.priceSum.toFixed(2) : ((data.minPrice !== null) ? data.minPrice.toFixed(2) : 0),
                currency: "EUR",
                items: [{
                    item_id: data.id,
                    id: data.id,
                    item_name: data.name,
                    currency: "EUR",
                    price: data.priceSum > 0 ? data.priceSum.toFixed(2) : ((data.minPrice !== null) ? data.minPrice.toFixed(2) : 0),
                    item_brand: data.region,
                    item_category: data.city,
                    index: props.position,
                    item_list_name: getEventType(),
                    item_list_id: getEventType(),
                    quantity: 1
                }],
            }
        }
        window.dataLayer.push({ecommerce: null});
        window.dataLayer.push(ga4Event);
    }

    useEffect(()=> {
        if(isInViewPort) {
            sendViewportEvent(props.data);
        }
    }, [isInViewPort]);

    return (
        <VillaContainer id={props.id} ref={ref} className={"villa-tile relative "+ (props.className ? props.className : '')} splitWidth={props.splitWidth} style={data.searchOpportunity  ? opportunityStyle : {}}>
            {data.searchOpportunity ? (
            <VillaTileOpportunity {...props}/> )
            : ""
            }
            <VillaTileImage 
                data={data}
                mobile={props.mobile}
                imageClassName="border-radius-default bor-bl-rad-no bor-br-rad-no"
                reviewType={props.reviewType}
                reviewStarText={props.reviewStarText}
                vidFavButtonsPosition={props.vidFavButtonsPosition}
                favouriteVillas={props.favouriteVillas}
                slideshowImagesPreloadCount={props.slideshowImagesPreloadCount}
                slideshowImagesLazyloadNext={props.slideshowImagesLazyloadNext}
                sendClickEvent={() => {sendClickEvent(data)}}
            />
            <div className="bg-grey3 p-3 border-radius-default bor-tl-rad-no bor-tr-rad-no relative">
                <VillaTileIcons 
                    data={data}
                    className="flex font-90 justify-sbetween text-center blue1"
                    iconHeight="17"
                />
                <VillaTileName
                    className="my-3"
                    data={data}
                    vidFavButtonsPosition={props.vidFavButtonsPosition}
                    favouriteVillas={props.favouriteVillas}
                />
                <VillaTileBreadcrumb 
                    data={data} 
                    className="font-90" 
                    linkClassName="grey1 underline"
                />
                <VillaTilePriceCTA 
                    data={data} 
                    className="mt-3"
                    mobile={props.mobile}
                    tablet={props.tablet}
                    currency={props.currency}
                    sendClickEvent={() => { sendClickEvent(data)}}
                />
            </div>
        </VillaContainer>
    );
}